import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { 
  FiShield, 
  FiUsers, 
  FiCheck, 
  FiX, 
  FiLock, 
  FiUnlock, 
  FiRefreshCw,
  FiAlertTriangle,
  FiInfo
} from 'react-icons/fi';

const GlobalBlacklistAccess = () => {
  const { user } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchAgencies();
    }
  }, [user]);

  const fetchAgencies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/blacklist/access/agencies');
      setAgencies(response.data.data);
    } catch (error) {
      console.error('Error fetching agencies:', error);
      toast.error('Erreur lors de la récupération des agences');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAccess = async () => {
    try {
      if (!reason.trim()) {
        toast.error('Veuillez fournir une raison');
        return;
      }

      const response = await api.post('/api/blacklist/access/grant', {
        agencyId: selectedAgency._id,
        reason: reason.trim()
      });

      toast.success(response.data.message);
      setShowGrantModal(false);
      setSelectedAgency(null);
      setReason('');
      fetchAgencies();
    } catch (error) {
      console.error('Error granting access:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'octroi de l\'accès');
    }
  };

  const handleRevokeAccess = async () => {
    try {
      if (!reason.trim()) {
        toast.error('Veuillez fournir une raison');
        return;
      }

      const response = await api.post('/api/blacklist/access/revoke', {
        agencyId: selectedAgency._id,
        reason: reason.trim()
      });

      toast.success(response.data.message);
      setShowRevokeModal(false);
      setSelectedAgency(null);
      setReason('');
      fetchAgencies();
    } catch (error) {
      console.error('Error revoking access:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la révocation de l\'accès');
    }
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center">
          <FiShield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Accès Refusé
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Seuls les superadmins peuvent accéder à cette page
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          🔐 Contrôle d'Accès à la Liste Noire Globale
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Gérez les permissions d'accès à la liste noire globale pour chaque agence
        </p>
      </div>

      {/* Global Access Status Alert */}
      {agencies.length > 0 && agencies.filter(a => a.canAccessGlobalBlacklist).length === 0 && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center">
            <FiAlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                ⚠️ Aucune agence n'a accès à la liste noire globale
              </p>
              <p className="text-sm text-red-700 dark:text-red-300">
                Actuellement, {agencies.length} agence(s) n'ont pas accès à la liste noire globale. 
                Accordez l'accès aux agences de confiance pour activer le partage des informations de sécurité.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Action for Superadmin's Own Agency */}
      {user && agencies.find(a => a._id === user.id && !a.canAccessGlobalBlacklist) && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FiInfo className="w-5 h-5 text-blue-600 mr-2" />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  💡 Accès à votre propre agence
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Votre agence n'a pas encore accès à la liste noire globale. 
                  Vous pouvez vous accorder l'accès immédiatement.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                const ownAgency = agencies.find(a => a._id === user.id);
                if (ownAgency) {
                  setSelectedAgency(ownAgency);
                  setReason('Accès accordé par le superadmin pour sa propre agence');
                  setShowGrantModal(true);
                }
              }}
              className="btn btn-primary text-sm px-4 py-2"
            >
              <FiUnlock className="w-4 h-4 mr-1" />
              S'Accorder l'Accès
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
              <FiCheck className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Accès Accordé
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {agencies.filter(a => a.canAccessGlobalBlacklist).length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
              <FiX className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Accès Refusé
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {agencies.filter(a => !a.canAccessGlobalBlacklist).length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
              <FiUsers className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Agences
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {agencies.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Agencies Table */}
      <div className="card overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Statut des Agences
            </h2>
            <button
              onClick={fetchAgencies}
              className="btn btn-secondary flex items-center gap-2"
            >
              <FiRefreshCw className="w-4 h-4" />
              Actualiser
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Agence
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Statut d'Accès
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Clients Blacklistés
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Détails d'Accès
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {agencies.map((agency) => (
                <tr key={agency._id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {agency.name || agency.company || 'Agence sans nom'}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {agency.email}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {agency.canAccessGlobalBlacklist ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        <FiUnlock className="w-3 h-3 mr-1" />
                        Accès Accordé
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        <FiLock className="w-3 h-3 mr-1" />
                        Accès Refusé
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {agency.blacklistCount || 0} clients
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {agency.canAccessGlobalBlacklist ? (
                      <div>
                        <div>Accordé le: {new Date(agency.globalBlacklistAccessGrantedAt).toLocaleDateString('fr-FR')}</div>
                        {agency.globalBlacklistAccessGrantedBy && (
                          <div>Par: {agency.globalBlacklistAccessGrantedBy.name || agency.globalBlacklistAccessGrantedBy.email}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">Aucun accès</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {agency.canAccessGlobalBlacklist ? (
                      <button
                        onClick={() => {
                          setSelectedAgency(agency);
                          setShowRevokeModal(true);
                        }}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 flex items-center gap-1"
                      >
                        <FiLock className="w-4 h-4" />
                        Révoquer
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedAgency(agency);
                          setShowGrantModal(true);
                        }}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 flex items-center gap-1"
                      >
                        <FiUnlock className="w-4 h-4" />
                        Accorder
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary and Recommendations */}
      {agencies.length > 0 && (
        <div className="mt-8">
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              📊 Résumé et Recommandations
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Agencies Without Access */}
              <div>
                <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Agences sans accès global ({agencies.filter(a => !a.canAccessGlobalBlacklist).length})
                </h4>
                <div className="space-y-2">
                  {agencies.filter(a => !a.canAccessGlobalBlacklist).map(agency => (
                    <div key={agency._id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {agency.name || agency.company || 'Agence sans nom'}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 block">
                          {agency.email}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedAgency(agency);
                          setShowGrantModal(true);
                        }}
                        className="btn btn-primary text-xs px-2 py-1"
                      >
                        <FiUnlock className="w-3 h-3 mr-1" />
                        Accorder
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Agencies With Access */}
              <div>
                <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Agences avec accès global ({agencies.filter(a => a.canAccessGlobalBlacklist).length})
                </h4>
                <div className="space-y-2">
                  {agencies.filter(a => a.canAccessGlobalBlacklist).map(agency => (
                    <div key={agency._id} className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded">
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {agency.name || agency.company || 'Agence sans nom'}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 block">
                          Accès accordé le {new Date(agency.globalBlacklistAccessGrantedAt).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedAgency(agency);
                          setShowRevokeModal(true);
                        }}
                        className="btn btn-danger text-xs px-2 py-1"
                      >
                        <FiLock className="w-3 h-3 mr-1" />
                        Révoquer
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recommendations */}
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h4 className="text-md font-medium text-blue-800 dark:text-blue-200 mb-2">
                💡 Recommandations
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                {agencies.filter(a => !a.canAccessGlobalBlacklist).length > 0 && (
                  <li>• Accordez l'accès aux agences de confiance pour améliorer la sécurité collective</li>
                )}
                {agencies.filter(a => a.canAccessGlobalBlacklist).length === 0 && (
                  <li>• Commencez par accorder l'accès à votre propre agence pour tester le système</li>
                )}
                <li>• Surveillez l'utilisation de la liste noire globale par les agences</li>
                <li>• Révoquez l'accès si une agence abuse du système</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Grant Access Modal */}
      {showGrantModal && selectedAgency && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Accorder l'Accès à la Liste Noire Globale
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Vous êtes sur le point d'accorder l'accès à la liste noire globale à :
              </p>
              <p className="font-medium text-gray-900 dark:text-white">
                {selectedAgency.name || selectedAgency.company || 'Agence sans nom'}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Raison de l'octroi d'accès *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Expliquez pourquoi vous accordez l'accès..."
                rows="3"
                className="input w-full"
                required
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowGrantModal(false);
                  setSelectedAgency(null);
                  setReason('');
                }}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleGrantAccess}
                className="btn btn-primary"
              >
                Accorder l'Accès
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Access Modal */}
      {showRevokeModal && selectedAgency && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Révoquer l'Accès à la Liste Noire Globale
            </h3>
            
            <div className="mb-4">
              <div className="flex items-center p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg mb-4">
                <FiAlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
                <span className="text-sm text-yellow-800 dark:text-yellow-200">
                  Attention : Cette action révoquera immédiatement l'accès de l'agence à la liste noire globale.
                </span>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Vous êtes sur le point de révoquer l'accès à la liste noire globale pour :
              </p>
              <p className="font-medium text-gray-900 dark:text-white">
                {selectedAgency.name || selectedAgency.company || 'Agence sans nom'}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Raison de la révocation *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Expliquez pourquoi vous révoquez l'accès..."
                rows="3"
                className="input w-full"
                required
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRevokeModal(false);
                  setSelectedAgency(null);
                  setReason('');
                }}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleRevokeAccess}
                className="btn btn-danger"
              >
                Révoquer l'Accès
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalBlacklistAccess;
